import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConnectionService } from '../../service/connection.service';
import { SwalService } from '../../service/swal.service';
import { TranslateService } from '@ngx-translate/core';
import { CHANGELOG } from '../novita/novita.component';
import { IconComponent } from '../../components/icon/icon.component';

type AdminTab = 'stats' | 'users' | 'feedback' | 'news' | 'roadmap' | 'waitlist';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
  imports: [CommonModule, FormsModule, IconComponent],
})
export class AdminComponent implements OnInit {
  stats: any = null;
  readonly currentRole = localStorage.getItem('user_role') ?? '';
  get isReadonly(): boolean { return this.currentRole === 'admin_readonly'; }
  get isSupport(): boolean { return this.currentRole === 'support'; }
  get isStats(): boolean { return this.currentRole === 'stats'; }
  get isRoadmap(): boolean { return this.currentRole === 'roadmap'; }
  get canWrite(): boolean { return ['admin', 'support'].includes(this.currentRole) || !this.currentRole; }
  get canFullAdmin(): boolean { return this.currentRole === 'admin' || !this.currentRole; }
  get canWriteRoadmap(): boolean { return ['admin', 'roadmap'].includes(this.currentRole) || !this.currentRole; }
  users: any[] = [];
  filteredUsers: any[] = [];
  searchQuery = '';
  filterStatus = 'all';
  activeTab: AdminTab = 'stats';
  feedbacks: any[] = [];
  waitlist: any[] = [];
  readonly latestChangelog = CHANGELOG[0];
  newsRecipientCount: number | null = null;
  isSendingNews = false;
  resetPasswordMap: { [id: string]: string } = {};
  showResetInput: { [id: string]: boolean } = {};
  openMenuId: string | null = null;
  menuTop = 0;
  menuRight = 0;

  // ── Vendita abbonamenti + richieste ──
  checkoutEnabled = false;
  togglingCheckout = false;
  planRequests: any[] = [];

  // ── Roadmap ──
  roadmap: any[] = [];
  roadmapGroupsView: { category: string; items: any[] }[] = [];
  roadmapCountsView = { todo: 0, doing: 0, done: 0 };
  roadmapFilter: 'all' | 'todo' | 'doing' | 'done' = 'all';
  editingRoadmapId: string | null = null;
  roadmapDraft: any = null;
  newRoadmap: any = { title: '', description: '', priority: 'media', category: '' };
  showNewRoadmap = false;
  readonly ROADMAP_STATUSES = [
    { value: 'todo',  label: 'Da fare' },
    { value: 'doing', label: 'In corso' },
    { value: 'done',  label: 'Fatto' },
  ];
  readonly ROADMAP_PRIORITIES = [
    { value: 'alta',  label: 'Alta' },
    { value: 'media', label: 'Media' },
    { value: 'bassa', label: 'Bassa' },
  ];

  constructor(
    private connection: ConnectionService,
    private swal: SwalService,
    private translate: TranslateService,
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  // ── MOBILE — griglia di sezioni + pannello utente dal basso ─────────────
  // Su mobile la pagina parte da una griglia di riquadri (mobileSection = null);
  // toccando un riquadro si entra nella sezione, con "indietro" per tornare alla griglia.
  readonly mobileSections: { key: AdminTab; label: string; icon: string; accent?: boolean }[] =
    this.buildMobileSections();
  mobileSection: AdminTab | null = null;
  sheetUserId: string | null = null;

  private buildMobileSections() {
    const all: { key: AdminTab; label: string; icon: string; accent?: boolean; visible: boolean }[] = [
      { key: 'stats', label: 'Statistiche', icon: 'chart', visible: !this.isRoadmap },
      { key: 'users', label: 'Utenti', icon: 'users', accent: true, visible: !this.isStats && !this.isRoadmap },
      { key: 'feedback', label: 'Feedback', icon: 'chat', visible: !this.isStats && !this.isSupport && !this.isRoadmap },
      { key: 'news', label: 'Novità app', icon: 'send', accent: true, visible: this.canFullAdmin },
      { key: 'waitlist', label: "Lista d'attesa", icon: 'hourglass', visible: this.canFullAdmin },
      { key: 'roadmap', label: 'Roadmap', icon: 'map', accent: true, visible: true },
    ];
    return all.filter((s) => s.visible).map(({ visible, ...s }) => s);
  }

  openMobileSection(key: AdminTab) {
    if (key === 'news') this.openNews();
    else if (key === 'waitlist') this.openWaitlist();
    else if (key === 'roadmap') this.openRoadmap();
    else this.activeTab = key;
    this.mobileSection = key;
    window.scrollTo(0, 0);
  }

  closeMobileSection() {
    this.mobileSection = null;
    window.scrollTo(0, 0);
  }

  sectionLabel(key: AdminTab | null): string {
    return this.mobileSections.find((s) => s.key === key)?.label ?? '';
  }

  /** Riga secondaria del riquadro: un numero utile, senza chiamate API in più. */
  sectionHint(key: AdminTab): string {
    switch (key) {
      case 'stats': return this.stats ? `${this.stats.totalUsers} utenti` : '…';
      case 'users': return `${this.users.length} iscritti`;
      case 'feedback': return `${this.feedbacks.length} ricevuti`;
      case 'news': return `v${this.latestChangelog.version}`;
      case 'waitlist': return this.waitlist.length ? `${this.waitlist.length} iscrizioni` : 'Iscrizioni landing';
      case 'roadmap': return `${this.roadmapCountsView.todo + this.roadmapCountsView.doing} aperte`;
    }
  }

  setFilterStatus(status: string) {
    this.filterStatus = status;
    this.applyFilters();
  }

  get sheetUser(): any {
    return this.sheetUserId ? this.getUserById(this.sheetUserId) : null;
  }

  openUserSheet(user: any) {
    this.sheetUserId = user._id;
  }

  closeUserSheet() {
    this.sheetUserId = null;
  }

  initials(user: any): string {
    return `${user?.name?.[0] ?? ''}${user?.lastname?.[0] ?? ''}`.toUpperCase() || '?';
  }

  readonly ACCOUNT_STATUS_LABELS: Record<string, string> = {
    active: 'Attivo', pending: 'In attesa', disabled: 'Disabilitato',
  };

  ngOnInit() {
    if (this.isRoadmap) {
      this.activeTab = 'roadmap';
      this.mobileSection = 'roadmap';
      this.loadRoadmap();
      return;
    }
    this.loadStats();
    this.loadUsers();
    this.loadFeedbacks();
    this.loadConfig();
    this.loadPlanRequests();
    this.loadRoadmap();
  }

  // ── Vendita abbonamenti + richieste ──────────────────────────────────────
  loadConfig() {
    this.connection.get(
      'admin/config',
      (d: any) => (this.checkoutEnabled = !!d?.checkoutEnabled),
      () => {},
    );
  }

  toggleCheckout() {
    if (this.togglingCheckout) return;
    const next = !this.checkoutEnabled;
    this.togglingCheckout = true;
    this.connection.patch(
      'admin/config/checkout',
      { enabled: next },
      (d: any) => {
        this.checkoutEnabled = !!d?.checkoutEnabled;
        this.togglingCheckout = false;
        this.swal.success(this.checkoutEnabled ? 'Vendita attivata' : 'Vendita disattivata');
      },
      () => {
        this.togglingCheckout = false;
        this.swal.error(this.t('swal.operation_failed'));
      },
    );
  }

  loadPlanRequests() {
    this.connection.get(
      'admin/plan-requests',
      (d: any) => (this.planRequests = d || []),
      () => {},
    );
  }

  get pendingRequests(): any[] {
    return this.planRequests.filter((r) => r.status === 'pending');
  }

  approveRequest(r: any) {
    this.swal
      .confirm(
        'Approva richiesta',
        `Assegnare il piano ${this.PLAN_LABELS[r.plan] || r.plan} a ${r.user?.name || 'questo utente'}?`,
        'Approva',
      )
      .then((res: any) => {
        if (!res?.isConfirmed) return;
        this.connection.post(
          `admin/plan-requests/${r._id}/approve`,
          {},
          () => {
            r.status = 'approved';
            this.swal.success('Piano assegnato');
          },
          () => this.swal.error(this.t('swal.operation_failed')),
        );
      });
  }

  dismissRequest(r: any) {
    this.connection.post(
      `admin/plan-requests/${r._id}/dismiss`,
      {},
      () => (r.status = 'dismissed'),
      () => this.swal.error(this.t('swal.operation_failed')),
    );
  }

  // ── ROADMAP ─────────────────────────────────────────────────────────────
  openRoadmap() {
    this.activeTab = 'roadmap';
    if (!this.roadmap.length) this.loadRoadmap();
  }

  loadRoadmap() {
    this.connection.get(
      'admin/roadmap',
      (d: any) => {
        this.roadmap = d || [];
        this.refreshRoadmapView();
      },
      () => {},
    );
  }

  setRoadmapFilter(filter: 'all' | 'todo' | 'doing' | 'done') {
    this.roadmapFilter = filter;
    this.refreshRoadmapView();
  }

  refreshRoadmapView() {
    this.roadmapCountsView = {
      todo: this.roadmap.filter((i) => i.status === 'todo').length,
      doing: this.roadmap.filter((i) => i.status === 'doing').length,
      done: this.roadmap.filter((i) => i.status === 'done').length,
    };
    const list =
      this.roadmapFilter === 'all'
        ? this.roadmap
        : this.roadmap.filter((i) => i.status === this.roadmapFilter);
    const rank: any = { alta: 0, media: 1, bassa: 2 };
    const sorted = [...list].sort((a, b) => (rank[a.priority] ?? 1) - (rank[b.priority] ?? 1));
    const map = new Map<string, any[]>();
    for (const item of sorted) {
      const cat = item.category || 'Senza categoria';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    }
    this.roadmapGroupsView = Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }

  trackRoadmapGroup(_i: number, g: { category: string }): string {
    return g.category;
  }

  trackRoadmapItem(_i: number, item: any): string {
    return item._id;
  }

  statusLabel(value: string): string {
    return this.ROADMAP_STATUSES.find((s) => s.value === value)?.label ?? value;
  }

  priorityLabel(value: string): string {
    return this.ROADMAP_PRIORITIES.find((p) => p.value === value)?.label ?? value;
  }

  startEditRoadmap(item: any) {
    if (!this.canWriteRoadmap) return;
    this.editingRoadmapId = item._id;
    this.roadmapDraft = { ...item };
  }

  cancelEditRoadmap() {
    this.editingRoadmapId = null;
    this.roadmapDraft = null;
  }

  saveEditRoadmap() {
    if (!this.roadmapDraft) return;
    const id = this.editingRoadmapId;
    const body = {
      title: this.roadmapDraft.title,
      description: this.roadmapDraft.description,
      category: this.roadmapDraft.category,
      status: this.roadmapDraft.status,
      priority: this.roadmapDraft.priority,
    };
    this.connection.patch(
      `admin/roadmap/${id}`,
      body,
      (d: any) => {
        const idx = this.roadmap.findIndex((i) => i._id === id);
        if (idx >= 0) this.roadmap[idx] = d;
        this.cancelEditRoadmap();
        this.refreshRoadmapView();
      },
      () => this.swal.error(this.t('swal.operation_failed')),
    );
  }

  setRoadmapStatus(item: any, status: string) {
    if (!this.canWriteRoadmap || item.status === status) return;
    this.connection.patch(
      `admin/roadmap/${item._id}`,
      { status },
      (d: any) => {
        item.status = d.status;
        this.refreshRoadmapView();
      },
      () => this.swal.error(this.t('swal.operation_failed')),
    );
  }

  setRoadmapPriority(item: any, priority: string) {
    if (!this.canWriteRoadmap || item.priority === priority) return;
    this.connection.patch(
      `admin/roadmap/${item._id}`,
      { priority },
      (d: any) => {
        item.priority = d.priority;
        this.refreshRoadmapView();
      },
      () => this.swal.error(this.t('swal.operation_failed')),
    );
  }

  addRoadmapItem() {
    const title = (this.newRoadmap.title || '').trim();
    if (!title) return;
    this.connection.post(
      'admin/roadmap',
      { ...this.newRoadmap, title },
      (d: any) => {
        this.roadmap.push(d);
        this.newRoadmap = { title: '', description: '', priority: 'media', category: '' };
        this.showNewRoadmap = false;
        this.refreshRoadmapView();
      },
      () => this.swal.error(this.t('swal.operation_failed')),
    );
  }

  deleteRoadmapItem(item: any) {
    this.swal
      .confirm('Elimina voce', `Eliminare "${item.title}"?`, 'Elimina')
      .then((res: any) => {
        if (!res?.isConfirmed) return;
        this.connection.delete(
          `admin/roadmap/${item._id}`,
          () => {
            this.roadmap = this.roadmap.filter((i) => i._id !== item._id);
            this.refreshRoadmapView();
          },
          () => this.swal.error(this.t('swal.operation_failed')),
        );
      });
  }

  // ── NOVITÀ APP ──────────────────────────────────────────────────────────
  openNews() {
    this.activeTab = 'news';
    this.connection.get(
      'admin/app-news/recipients',
      (data: any) => (this.newsRecipientCount = data?.count ?? 0),
      () => (this.newsRecipientCount = null),
    );
  }

  get newsItems(): string[] {
    return (this.latestChangelog?.sections ?? []).flatMap((s) => s.items ?? []);
  }

  sendAppNews() {
    const count = this.newsRecipientCount ?? 0;
    if (count === 0) {
      this.swal.error('Nessun utente ha attivato le notifiche "Novità app".');
      return;
    }
    this.swal
      .confirm(
        'Invia novità app',
        `Inviare l'email della versione ${this.latestChangelog.version} a ${count} utenti?`,
      )
      .then((result) => {
        if (!result.isConfirmed) return;
        this.isSendingNews = true;
        this.connection.post(
          'admin/app-news',
          { version: this.latestChangelog.version, items: this.newsItems },
          (data: any) => {
            this.isSendingNews = false;
            this.swal.success(`Email inviate: ${data?.sent ?? 0}`);
          },
          () => {
            this.isSendingNews = false;
            this.swal.error('Invio fallito');
          },
        );
      });
  }

  loadFeedbacks() {
    this.connection.get(
      'feedback/all',
      (data: any) => (this.feedbacks = data),
      () => {},
    );
  }

  openWaitlist() {
    this.activeTab = 'waitlist';
    this.connection.get(
      'admin/waitlist',
      (data: any) => (this.waitlist = data),
      () => {},
    );
  }

  get activePercent(): number {
    if (!this.stats?.totalUsers) return 0;
    return Math.round((this.stats.activeUsers / this.stats.totalUsers) * 100);
  }

  get signedPercent(): number {
    if (!this.stats?.totalEvents) return 0;
    return Math.round((this.stats.totalContracts / this.stats.totalEvents) * 100);
  }

  get maxMonthCount(): number {
    if (!this.stats?.eventsByMonth?.length) return 1;
    return Math.max(...this.stats.eventsByMonth.map((m: any) => m.count));
  }

  barHeight(count: number): number {
    return Math.max(4, Math.round((count / this.maxMonthCount) * 100));
  }

  monthLabel(m: any): string {
    const months = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    return months[(m._id.month - 1)] ?? m._id.month;
  }

  avgNps(): number {
    if (!this.feedbacks.length) return 0;
    const sum = this.feedbacks.reduce((acc, f) => acc + (f.nps ?? 0), 0);
    return Math.round((sum / this.feedbacks.length) * 10) / 10;
  }

  interestedCount(): number {
    return this.feedbacks.filter((f) => f.interested_subscription).length;
  }
  loadStats() {
    this.connection.get(
      'admin/stats',
      (data: any) => {
        this.stats = data;
      },
      (err: any) => {
        console.error('stats error:', err);
      },
    );
  }

  loadUsers() {
    this.connection.get(
      'admin/users',
      (data: any) => {
        this.users = data;
        this.applyFilters();
      },
      (err: any) =>
        this.swal.errorDialog('Errore', 'Impossibile caricare gli utenti'),
    );
  }

  applyFilters() {
    this.filteredUsers = this.users.filter((u) => {
      const matchSearch =
        !this.searchQuery ||
        u.name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        u.lastname?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        u.username?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(this.searchQuery.toLowerCase());

      const matchStatus =
        this.filterStatus === 'all' || u.status === this.filterStatus;

      return matchSearch && matchStatus;
    });
  }

  toggleUser(user: any) {
    const disabled = user.status === 'disabled';
    this.swal
      .confirm(
        this.t(disabled ? 'swal.confirm_enable' : 'swal.confirm_disable', {
          name: user.name,
        }),
        undefined,
        this.t('swal.yes'),
      )
      .then((result) => {
        if (!result.isConfirmed) return;
        this.connection.patch(
          `admin/users/${user._id}/toggle`,
          {},
          (data: any) => {
            user.status = data.status;
            this.applyFilters();
            this.swal.success(
              this.t(
                data.status === 'disabled'
                  ? 'swal.user_disabled'
                  : 'swal.user_enabled',
              ),
            );
          },
          () => this.swal.error(this.t('swal.operation_failed')),
        );
      });
  }

  deleteUser(user: any) {
    this.swal
      .confirm(
        this.t('swal.confirm_delete_user', {
          name: `${user.name} ${user.lastname}`,
        }),
        this.t('swal.delete_user_warning'),
        this.t('swal.delete'),
      )
      .then((result) => {
        if (!result.isConfirmed) return;
        this.connection.delete(
          `admin/users/${user._id}`,
          () => {
            this.users = this.users.filter((u) => u._id !== user._id);
            this.applyFilters();
            this.swal.success(this.t('swal.user_deleted'));
          },
          () => this.swal.error(this.t('swal.delete_failed')),
        );
      });
  }

  toggleMenu(id: string, event: MouseEvent) {
    if (this.openMenuId === id) { this.openMenuId = null; return; }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 180; // min-width di .action-menu
    const menuMaxHeight = 320; // deve combaciare con max-height in admin.component.css
    const margin = 8;
    // Clamp verticale: se il bottone è vicino al fondo dello schermo (tipico
    // nella card mobile), il menu deve restare dentro il viewport invece di
    // aprirsi sotto e finire irraggiungibile (nessuno scroll lo segue, essendo fixed).
    this.menuTop = Math.min(
      Math.max(rect.bottom + 4, margin),
      window.innerHeight - menuMaxHeight - margin,
    );
    const rawRight = window.innerWidth - rect.right;
    // Clamp orizzontale: sulla card mobile il bottone è allineato a sinistra,
    // non a destra come nella tabella desktop — senza clamp il menu finisce fuori schermo.
    this.menuRight = Math.min(
      Math.max(rawRight, margin),
      window.innerWidth - menuWidth - margin,
    );
    this.openMenuId = id;
  }

  @HostListener('document:click')
  closeMenu() {
    this.openMenuId = null;
  }

  toggleResetInput(id: string) {
    this.showResetInput[id] = !this.showResetInput[id];
    if (!this.resetPasswordMap[id]) this.resetPasswordMap[id] = '';
    this.openMenuId = null;
  }

  resetPassword(user: any) {
    const newPassword = this.resetPasswordMap[user._id];
    if (!newPassword || newPassword.length < 6) {
      this.swal.warning(this.t('swal.error'), this.t('swal.password_min'));
      return;
    }
    this.connection.patch(
      `admin/users/${user._id}/reset-password`,
      { password: newPassword },
      () => {
        this.showResetInput[user._id] = false;
        this.resetPasswordMap[user._id] = '';
        this.swal.success(this.t('swal.password_updated'));
      },
      () => this.swal.error(this.t('swal.password_reset_failed')),
    );
  }

  getActivityLabel(user: any): string {
    return user.professional_category || '—';
  }

  roleMenuUserId: string | null = null;

  toggleRoleMenu(userId: string) {
    this.roleMenuUserId = this.roleMenuUserId === userId ? null : userId;
    this.openMenuId = null;
    this.sheetUserId = null;
  }

  applyRole(user: any, role: string) {
    this.roleMenuUserId = null;
    this.connection.patch(
      `admin/users/${user._id}/role`,
      { role },
      (data: any) => {
        user.role = data.role;
        this.swal.success(this.t('swal.role_updated'));
      },
      () => this.swal.error(this.t('swal.operation_failed')),
    );
  }

  getUserById(id: string): any {
    return this.filteredUsers.find(u => u._id === id);
  }

  readonly ROLES = [
    { value: '',               label: 'Utente normale',     desc: 'Nessun accesso admin' },
    { value: 'stats',          label: 'Statistiche',        desc: 'Solo tab statistiche' },
    { value: 'support',        label: 'Support',            desc: 'Vedi utenti, reset password' },
    { value: 'roadmap',        label: 'Roadmap',            desc: 'Solo tab roadmap, in modifica' },
    { value: 'admin_readonly', label: 'Admin readonly',     desc: 'Tutto in sola lettura' },
    { value: 'admin',          label: 'Admin completo',     desc: 'Accesso totale' },
  ];

  formatDate(date: string): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('it-IT');
  }

  updateSubscription(user: any, plan: string, status: string) {
    this.connection.patch(
      `admin/users/${user._id}/subscription`,
      { plan, status },
      () => {
        user.subscription = { ...user.subscription, plan, status };
        this.swal.success(this.t('swal.subscription_updated'));
      },
      () => this.swal.error(this.t('swal.subscription_update_failed')),
    );
  }

  // ── Modale abbonamento ──
  subUserId: string | null = null;
  subDetail: any = null;
  subLoading = false;
  subProcessing = false;

  readonly PLAN_LABELS: Record<string, string> = {
    free: 'Free', base: 'Base', pro: 'Pro', agency: 'Agency',
  };
  readonly STATUS_LABELS: Record<string, string> = {
    trial: 'In prova', active: 'Attivo', inactive: 'Sospeso', cancelled: 'Disdetto',
  };

  /** Etichetta badge riga tabella: distingue una prova scaduta da una ancora attiva. */
  subStatusLabel(sub: any): string {
    if (sub?.status === 'active') return this.PLAN_LABELS[sub?.plan] || 'Free';
    if (sub?.status === 'trial' && sub?.trialExpired) return 'Prova scaduta';
    return this.STATUS_LABELS[sub?.status] || 'Free';
  }

  /** Etichetta stato nella modale dettaglio: usa l'entitlement calcolato dal backend. */
  subDetailStatusLabel(): string {
    const s = this.subDetail?.subscription;
    if (!s) return '';
    if (s.status === 'trial' && this.subDetail?.entitlements?.trialExpired) return 'Prova scaduta';
    return this.STATUS_LABELS[s.status] || s.status;
  }

  openSubscription(user: any) {
    this.subUserId = user._id;
    this.subDetail = null;
    this.subLoading = true;
    this.openMenuId = null;
    this.sheetUserId = null;
    this.loadSubDetail();
  }

  private loadSubDetail() {
    if (!this.subUserId) return;
    this.subLoading = true;
    this.connection.get(
      `admin/users/${this.subUserId}/subscription`,
      (data: any) => { this.subDetail = data; this.subLoading = false; },
      () => { this.subLoading = false; this.swal.error('Caricamento abbonamento fallito'); },
    );
  }

  closeSub() {
    this.subUserId = null;
    this.subDetail = null;
  }

  adminExtendTrial(days: number) {
    if (!this.subUserId || this.subProcessing) return;
    this.subProcessing = true;
    this.connection.post(
      `admin/users/${this.subUserId}/extend-trial`,
      { days },
      () => {
        this.subProcessing = false;
        this.swal.success(`Prova estesa di ${days} giorni`);
        this.syncRow('trial');
        this.loadSubDetail();
      },
      () => { this.subProcessing = false; this.swal.error(this.t('swal.operation_failed')); },
    );
  }

  adminSetPlan(plan: string) {
    if (!this.subUserId || this.subProcessing) return;
    const status = plan === 'free' ? 'cancelled' : 'active';
    this.subProcessing = true;
    this.connection.patch(
      `admin/users/${this.subUserId}/subscription`,
      { plan, status },
      () => {
        this.subProcessing = false;
        this.swal.success(plan === 'free' ? 'Abbonamento rimosso' : `Piano ${this.PLAN_LABELS[plan]} assegnato`);
        this.syncRow(status, plan);
        this.loadSubDetail();
      },
      () => { this.subProcessing = false; this.swal.error(this.t('swal.subscription_update_failed')); },
    );
  }

  private syncRow(status: string, plan?: string) {
    const row = this.getUserById(this.subUserId ?? '');
    if (!row) return;
    row.subscription = {
      ...row.subscription,
      status,
      trialExpired: false,
      ...(plan ? { plan } : {}),
    };
  }
}
